import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronLeft, Loader2, MoreHorizontal, Wand2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { formatDay } from "../lib/finance";
import {
  BLOCK_GROUPS, canWrite, cleanBlocks, coverBlockIndexes, emptyBlock, insertItem, layoutOf, moveItem, recordsError, unknownVariables, withVariable,
} from "../lib/records";
import { detachAt } from "../lib/clauses";
import { AddBlockMenu, BlockEditor, BlockInserter, LayoutCard, SectionBand } from "../components/records/BlockEditors";
import { ClauseCompareDialog, ClausePicker, DetachClauseDialog } from "../components/records/ClauseBits";
import { TemplateOutline, TemplatePreviewFrame, TemplateStatus } from "../components/records/TemplateBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { ResponsivePopover, ResponsivePopoverContent, ResponsivePopoverTrigger } from "../components/ui/responsive-popover";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";

const HISTORY = { created: "Modèle créé", updated: "Modèle modifié", draft_saved: "Brouillon enregistré", draft_started: "Nouvelle version commencée",
  published: "Version publiée", draft_discarded: "Brouillon abandonné" };

function RenameDialog({ template, open, onClose, onDone }) {
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setName(template.name); setDescription(template.description || ""); setError(""); } }, [open, template]);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/records/templates/${template.id}`, { expected_version: template.version, name: name.trim(), description });
      toast.success("Modèle modifié.");
      onDone(data);
    } catch (e) {
      setError(recordsError(e, "Enregistrement impossible."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Nom et description</DialogTitle><DialogDescription>Changer le nom ne modifie pas les documents déjà générés.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="template-rename">Nom</Label>
            <Input id="template-rename" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} className="h-10" /></div>
          <div className="space-y-1.5"><Label htmlFor="template-description">Description</Label>
            <Textarea id="template-description" value={description} rows={2} maxLength={4000} onChange={(e) => setDescription(e.target.value)} /></div>
        </div>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Fermer</Button>
          <Button onClick={submit} disabled={busy || name.trim().length < 2}>{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RecordTemplateEditor() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [variables, setVariables] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [layout, setLayout] = useState(layoutOf(null));
  const [assets, setAssets] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [tab, setTab] = useState("edit");
  const [dialog, setDialog] = useState(null);
  const [picking, setPicking] = useState(null);
  const [comparing, setComparing] = useState(null);
  const [detaching, setDetaching] = useState(null);
  const caret = useRef(null);
  const previewSeq = useRef(0);

  const apply = useCallback((data) => {
    setTemplate(data);
    setBlocks(data.draft ? data.draft.blocks : []);
    setLayout(layoutOf(data.draft));
    setNote(data.draft?.note || "");
  }, []);
  const load = useCallback(async () => {
    setLoadError("");
    try {
      const { data } = await api.get(`/records/templates/${id}`);
      apply(data);
    } catch (e) {
      setLoadError(recordsError(e, "Modèle introuvable."));
    }
  }, [id, apply]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (template?.capabilities?.manage) api.get("/records/brand/assets", { params: { include_retired: true } }).then(({ data }) => setAssets(data.items)).catch(() => {});
  }, [template?.capabilities?.manage]);
  useEffect(() => { api.get("/records/template-variables").then(({ data }) => setVariables(data.items)).catch(() => {}); }, []);

  const draft = template?.draft;
  const manage = !!template?.capabilities?.manage;
  const editing = manage && !!draft;
  const cleaned = useMemo(() => cleanBlocks(blocks), [blocks]);
  const dirty = editing && (JSON.stringify(cleaned) !== JSON.stringify(cleanBlocks(draft.blocks)) || note !== (draft.note || "")
    || JSON.stringify(layout) !== JSON.stringify(layoutOf(draft)));
  const unknown = useMemo(() => unknownVariables(blocks, variables), [blocks, variables]);

  // The preview follows the editor (unsaved content included) after a short pause; a reader sees the published version.
  useEffect(() => {
    if (!template) return undefined;
    if (!editing && !template.published) return undefined;
    if (unknown.length) return undefined;
    const mine = ++previewSeq.current;
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(`/records/templates/${template.id}/preview`, editing ? { blocks: cleaned, layout, paged: true } : { paged: true });
        if (mine === previewSeq.current) { setPreview(data); setPreviewError(""); }
      } catch (e) {
        if (mine === previewSeq.current) setPreviewError(recordsError(e, "Aperçu indisponible."));
      } finally {
        if (mine === previewSeq.current) setPreviewLoading(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [template, editing, cleaned, layout, unknown.length]);

  const run = async (label, request, success) => {
    setBusy(label);
    try {
      const { data } = await request();
      apply(data);
      toast.success(success);
      return data;
    } catch (e) {
      toast.error(recordsError(e, "Action impossible. Réessayez."));
      return null;
    } finally {
      setBusy("");
    }
  };
  const save = () => run("save", () => api.put(`/records/templates/${id}/draft`, { expected_version: draft.version, blocks: cleaned, layout, note }), "Brouillon enregistré.");
  const publish = async () => {
    let current = template;
    if (dirty) {
      current = await save();
      if (!current) return;
    }
    await run("publish", () => api.post(`/records/templates/${id}/publish`, { expected_version: current.draft.version }), "Version publiée : elle sert aux prochaines générations.");
  };

  const updateBlock = (index, next) => setBlocks((list) => list.map((b, i) => (i === index ? next : b)));
  const addBlock = (kind, at) => {
    caret.current = null;
    if (kind === "clause") { setPicking({ at }); return; }
    setBlocks((list) => insertItem(list, emptyBlock(kind), at));
  };
  const pickIn = (index, key) => {
    const done = withVariable(blocks[index], index, caret.current, key);
    updateBlock(index, done.block);
    caret.current = done.caret;
  };

  if (loadError) {
    return (
      <Card className="p-6 text-center space-y-3" data-testid="template-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">{loadError}</p>
        <Button variant="outline" asChild><Link to="/documents/modeles">Retour aux modèles</Link></Button>
      </Card>
    );
  }
  if (!template) return <div className="space-y-3"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-96 w-full" /></div>;

  const coverIdx = coverBlockIndexes(blocks, layout);
  const contentStart = layout.cover?.enabled ? blocks.findIndex((_, i) => !coverIdx.includes(i) && (coverIdx.length === 0 || i > coverIdx[coverIdx.length - 1])) : -1;
  const trail = manage ? (
    <div className={editing ? "grid gap-4 md:grid-cols-2" : "space-y-3"}>
      <Card className="p-4 space-y-2" data-testid="template-versions">
        <h2 className="text-sm font-semibold text-slate-900">Versions</h2>
        <ul className="space-y-1 text-sm text-slate-600">
          {(template.versions || []).map((v) => (
            <li key={v.id}>Version {v.number} · {{ draft: "brouillon", published: "publiée", deprecated: "remplacée" }[v.status]}
              {v.published_at ? ` le ${formatDay(v.published_at.slice(0, 10))}${v.published_by_name ? ` par ${v.published_by_name}` : ""}` : ""}{v.note ? ` — ${v.note}` : ""}</li>
          ))}
        </ul>
        <p className="text-xs text-slate-500">{template.generated_count || 0} document{template.generated_count > 1 ? "s" : ""} généré{template.generated_count > 1 ? "s" : ""} ; une nouvelle version ne les change jamais.</p>
      </Card>
      <Card className="p-4 space-y-2">
        <h2 className="text-sm font-semibold text-slate-900">Historique</h2>
        <ul className="space-y-1 text-sm text-slate-600">
          {(template.history || []).slice().reverse().slice(0, 12).map((h, i) => (
            <li key={i}>{HISTORY[h.kind] || h.kind}{h.data?.number ? ` (version ${h.data.number})` : ""} · {h.actor_name} · {new Date(h.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</li>
          ))}
        </ul>
      </Card>
    </div>
  ) : null;
  const canGenerate = template.capabilities.generate && canWrite(user, "records:create");
  const editor = editing ? (
    <div className="space-y-3" data-testid="template-editor">
      <LayoutCard layout={layout} onChange={setLayout} />
      {unknown.length > 0 && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="template-unknown">
          Variable inconnue : {unknown.map((v) => `{${v}}`).join(", ")}. Utilisez « Insérer une variable ».
        </p>
      )}
      {blocks.length === 0 && <p className="text-sm text-slate-500">Le brouillon est vide : ajoutez un titre, des articles et le bloc des signatures.</p>}
      <div data-testid="template-blocks">
      {layout.cover?.enabled && !coverIdx.length && (
        <SectionBand label="Page de garde" hint="Ajoutez un titre : il s'affichera sur la page de garde." testid="band-cover" />
      )}
      {blocks.map((b, i) => (
        <Fragment key={i}>
          {layout.cover?.enabled && i === coverIdx[0] && <SectionBand label="Page de garde" hint="Titre et sous-titre de la couverture" testid="band-cover" />}
          {layout.cover?.enabled && i === contentStart && <SectionBand label="Contenu" testid="band-content" />}
          {i > 0 && !(coverIdx.includes(i) && coverIdx.includes(i - 1)) && <BlockInserter index={i} groups={BLOCK_GROUPS} itemPrefix="template-add" onAdd={(kind) => addBlock(kind, i)} />}
          <BlockEditor block={b} index={i} count={blocks.length} assets={assets} variables={variables} onPickVariable={(key) => pickIn(i, key)}
            clauseStatus={template.clause_status?.[b.clause_id]}
            onClauseUpdate={() => setComparing(i)} onClauseDetach={() => setDetaching(i)} onChange={(next) => updateBlock(i, next)} onCaret={(c) => { caret.current = c; }}
            onMove={(delta) => { caret.current = null; setBlocks((list) => moveItem(list, i, delta)); }}
            onRemove={() => { caret.current = null; setBlocks((list) => list.filter((_, j) => j !== i)); }} />
        </Fragment>
      ))}
      </div>
      <div className="flex justify-center">
        <AddBlockMenu groups={BLOCK_GROUPS} itemPrefix="template-add" testid="template-add-block" onAdd={(kind) => addBlock(kind)} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="template-note">Note de version</Label>
        <Input id="template-note" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} placeholder="Ce qui change dans cette version" className="h-10" />
      </div>
    </div>
  ) : (
    <div className="space-y-3">
      <Card className="p-4 text-sm text-slate-600 space-y-2" data-testid="template-readonly">
        {manage ? (
          <>
            <p>La version {template.published_number} est publiée et ne se modifie plus. Pour changer le texte, commencez une nouvelle version : elle repart de celle-ci.</p>
            <Button size="sm" onClick={() => run("draft", () => api.post(`/records/templates/${id}/draft`), "Nouvelle version commencée.")} disabled={!!busy}
              data-testid="template-new-version">{busy === "draft" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Nouvelle version</Button>
          </>
        ) : <p>Version {template.published_number} publiée{template.published_at ? ` le ${formatDay(template.published_at.slice(0, 10))}` : ""}.</p>}
      </Card>
      <TemplateOutline blocks={template.published?.blocks} onBeforeGo={() => setTab("preview")} />
      {trail}
    </div>
  );
  const previewPane = (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-900">Aperçu{editing ? " du brouillon" : ""}</h2>
        {editing && <span className="text-xs text-slate-500">Une variable sans valeur est surlignée.</span>}
      </div>
      <TemplatePreviewFrame preview={preview} loading={previewLoading} error={previewError} maxHeightClassName="lg:max-h-[calc(100vh-9rem)] lg:overflow-y-auto" />
    </div>
  );

  return (
    <div className="space-y-4 pb-4" data-testid="template-page">
      <Link to="/documents/modeles" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ChevronLeft size={14} />Modèles de documents</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h1 className="font-display font-bold text-2xl text-slate-900 break-words">{template.name}</h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <TemplateStatus template={template} />
            {editing && <span>Brouillon de la version {draft.number}{dirty ? " · modifications non enregistrées" : ""}</span>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canGenerate && (
            <Button variant="outline" asChild data-testid="template-generate"><Link to={`/documents/generer?modele=${template.id}`}><Wand2 size={14} className="mr-1.5" />Générer un document</Link></Button>
          )}
          {editing && (
            <>
              <Button variant="outline" onClick={save} disabled={!dirty || !!busy || unknown.length > 0} data-testid="template-save">
                {busy === "save" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer
              </Button>
              <Button onClick={publish} disabled={!!busy || unknown.length > 0} data-testid="template-publish">
                {busy === "publish" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Publier
              </Button>
            </>
          )}
          {manage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Autres actions" data-testid="template-more"><MoreHorizontal size={16} /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" title={template.name}>
                <DropdownMenuItem onClick={() => setDialog("rename")}>Nom et description</DropdownMenuItem>
                {!template.published_number && hasPermission(user, "records:delete") && <DropdownMenuItem onClick={() => setDialog("delete")} className="text-red-600 focus:text-red-700" data-testid="template-delete">Supprimer le modèle…</DropdownMenuItem>}
                {editing && template.published_number && <DropdownMenuItem onClick={() => setDialog("discard")} data-testid="template-discard">Abandonner le brouillon</DropdownMenuItem>}
                <DropdownMenuItem onClick={() => run("active", () => api.patch(`/records/templates/${id}`, { expected_version: template.version, active: !template.active }),
                  template.active ? "Modèle désactivé : il ne sert plus aux générations." : "Modèle réactivé.")} data-testid="template-toggle">
                  {template.active ? "Désactiver le modèle" : "Réactiver le modèle"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="flex gap-1 lg:hidden" role="tablist" aria-label="Modifier ou voir l'aperçu">
        {[["edit", editing ? "Modifier" : "Version"], ["preview", "Aperçu"]].map(([value, label]) => (
          <button key={value} type="button" role="tab" aria-selected={tab === value} onClick={() => setTab(value)} data-testid={`template-tab-${value}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${tab === value ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{label}</button>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <div className={`min-w-0 ${tab === "edit" ? "" : "hidden lg:block"}`}>{editor}</div>
        <div className={`min-w-0 lg:sticky lg:top-4 ${tab === "preview" ? "" : "hidden lg:block"}`}>{previewPane}</div>
      </div>

      {editing && trail}

      <ClausePicker open={!!picking} onClose={() => setPicking(null)} onPick={(block) => { setBlocks((list) => insertItem(list, block, picking?.at)); setPicking(null); toast.success("Clause insérée : le modèle en garde une copie figée."); }} />
      <ClauseCompareDialog block={comparing != null ? blocks[comparing] : null} onClose={() => setComparing(null)}
        onConfirm={(block) => { updateBlock(comparing, block); setComparing(null); toast.success("Clause mise à jour : enregistrez le brouillon puis publiez une nouvelle version."); }} />
      <DetachClauseDialog block={detaching != null ? blocks[detaching] : null} onClose={() => setDetaching(null)}
        onConfirm={() => { setBlocks((list) => detachAt(list, detaching)); setDetaching(null); toast.success("Clause détachée : son texte est maintenant modifiable."); }} />
      <DeleteWithReasonDialog
        target={dialog === "delete" ? template : null} testId="delete-template" onClose={() => setDialog(null)}
        title={`Supprimer le modèle « ${template?.name} » ?`}
        description="Il va dans la corbeille et se restaure pendant 30 jours. Un modèle déjà publié, ou déjà utilisé pour générer un document, se désactive à la place."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/records/templates/${id}`, { data: { reason } });
            toast.success("Mis à la corbeille");
            navigate("/documents/modeles");
          } catch (e) {
            toast.error(recordsError(e, "Impossible de supprimer cet élément."));
            setDialog(null);
          }
        }}
      />
      <RenameDialog template={template} open={dialog === "rename"} onClose={() => setDialog(null)} onDone={(data) => { setDialog(null); apply(data); }} />
      <AlertDialog open={dialog === "discard"} onOpenChange={(v) => !v && setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Abandonner le brouillon de la version {draft?.number} ?</AlertDialogTitle>
            <AlertDialogDescription>Ses modifications sont perdues ; la version {template.published_number} reste publiée.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Garder le brouillon</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="template-discard-confirm"
              onClick={() => { setDialog(null); run("discard", () => api.delete(`/records/templates/${id}/draft`), "Brouillon abandonné."); }}>Abandonner</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
